import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import ReactSelectAsync from 'react-select/async';
import ReactSelect, { components as ReactSelectComponents } from 'react-select';
import { IntlContext } from 'react-intl';
import {
  Chip,
  InputAdornment as MuiInputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import FormHelperText from '@material-ui/core/FormHelperText';
import { withTheme } from '@material-ui/core/styles';
import { Box, Icon } from '@popmenu/common-ui';
import { Download, X as XIcon } from '@popmenu/web-icons';

import { useIntl } from '../../../utils/withIntl';
import { classNames, withStyles } from '../../../utils/withStyles';
import selectStyles from '../../../assets/jss/shared/selectStyles';
import { findElement, findElements } from '../../../utils/dom';
import { SELECT_CLASS_NAME_PREFIX } from './selectClassNamePrefix';

export const makeClearValueIndicatorFocusable = (wrapperRef, selectComponentRef, clearAriaLabel) => {
  const clearValueIndicator = findElement('[class*="pm-clear-indicator"]', wrapperRef);

  if (clearValueIndicator) {
    clearValueIndicator.role = 'button';
    clearValueIndicator.ariaLabel = clearAriaLabel || 'Close';
    clearValueIndicator.tabIndex = 0;
    clearValueIndicator.removeAttribute('aria-hidden');

    const childElements = clearValueIndicator.querySelectorAll('[aria-hidden="true"]');
    childElements.forEach((child) => {
      child.removeAttribute('aria-hidden');
    });

    clearValueIndicator.onkeydown = function clearValues(e) {
      // Clear values and focus on input
      if (e.key === 'Enter' || e.key === ' ') {
        // Prevent default behavior that opens select options
        e.preventDefault();
        selectComponentRef.clearValue();
        wrapperRef.querySelector('input:not([type="hidden"])')?.focus();
      }
      // Allow Tab and other keys to work normally
    };
  }
};

export const setAccessibilityAttributes = (wrapperRef, label, ariaLabelledBy, isExpanded, focusedOptionId = null, ariaRequired = false) => {
  findElements('input', wrapperRef).forEach((input) => {
    if (input.readOnly && !input.getAttribute('aria-label') && !input.getAttribute('aria-labelledby')) {
      if (ariaLabelledBy) {
        input.setAttribute('aria-labelledby', ariaLabelledBy);
      } else {
        input.setAttribute('aria-label', label);
      }
    }

    if (ariaRequired) {
      input.setAttribute('aria-required', 'true');
    }

    if (input.id && input.id.includes('react-select')) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', isExpanded);
      input.setAttribute('aria-haspopup', 'listbox');

      // The open menu renders a role="listbox" element (see Menu below); link the input to it.
      // A closed menu, or one portalled outside the wrapper, leaves nothing to link.
      const listbox = wrapperRef.querySelector('[role="listbox"]');
      if (listbox) {
        if (!listbox.id) {
          listbox.setAttribute('id', `${input.id.replace('-input', '')}-listbox`);
        }
        input.setAttribute('aria-controls', listbox.id);
      } else {
        input.removeAttribute('aria-controls');
      }

      // Add aria-activedescendant when an option is focused
      if (focusedOptionId) {
        input.setAttribute('aria-activedescendant', focusedOptionId);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
  });
};

const NoOptionsMessage = props => (
  <Typography
    color="textSecondary"
    className={props.selectProps.classes.noOptionsMessage}
    {...props.innerProps}
  >
    {props.selectProps.noOptionsMessageProps.label}
  </Typography>
);

const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;

const Control = (props) => {
  // Start Adornment
  let startAdornment = null;
  if (props.selectProps && props.selectProps.startIcon) {
    startAdornment = (
      <MuiInputAdornment position="start">
        {typeof props.selectProps.startIcon === 'string' ?
          <Icon icon={props.selectProps.startIcon} /> :
          React.createElement(props.selectProps.startIcon, props.selectProps.startIconProps)}
      </MuiInputAdornment>
    );
  }

  // react-select exposes the control's mouse/touch handlers on innerProps. MUI v5+
  // renders the field label as a sibling of the input wrapper, so a click on the
  // label never reaches handlers bound to the wrapper and the dropdown won't open.
  // Bind them to the TextField root to cover the label too, and remove from innerProps
  // to prevent double-firing when clicking the input.
  const { onMouseDown, onTouchEnd, ...innerProps } = props.innerProps;

  return (
    <TextField
      fullWidth
      onMouseDown={onMouseDown}
      onTouchEnd={onTouchEnd}
      InputProps={{
        inputComponent,
        inputProps: {
          children: props.children,
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          ...innerProps,
        },
        startAdornment,
      }}
      {...props.selectProps.textFieldProps}
    />
  );
};

const Option = (props) => {
  // Generate a stable ID for this option for ARIA
  // Use the option's index from react-select's options array
  const optionId = props.innerProps.id || `${props.selectProps.inputId || 'react-select'}-option-${props.value}`;
  const prevFocusedRef = React.useRef();

  React.useEffect(() => {
    // Only notify if focus state actually changed
    if (prevFocusedRef.current !== props.isFocused) {
      if (props.isFocused && props.selectProps.onFocusOption) {
        props.selectProps.onFocusOption(optionId);
      }

      prevFocusedRef.current = props.isFocused;
    }
  });

  const MenuItemLabelClass = props.selectProps.classes.menuItemLabel;
  const MenuItemRootClass = classNames(
    props.selectProps.classes.menuItemRoot,
    props.selectProps.wrap ? props.selectProps.classes.wrap : null,
  );

  return (
    <MenuItem
      aria-selected={props.isSelected}
      disabled={props.data.disabled}
      className={MenuItemRootClass}
      component="div"
      ref={props.innerRef}
      role="option"
      selected={props.isFocused}
      style={{
        backgroundColor: props.isFocused && props.theme.colors.neutral30,
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.selectProps.hoverOptions ? (
        <Tooltip title={props.label}>
          <Box data-cy="option">
            <Box display="flex">
              {props.data.useIcon && (
                <Box mr={1}>
                  <Icon
                    aria-label={props.label || 'Download'}
                    color={props.data.iconColor || 'default'}
                    icon={Download}
                  />
                </Box>
              )}
              <Box className={MenuItemLabelClass}>
                {props.children}
              </Box>
            </Box>
            {props.data.useSubtitle && (
              <Box ml={3}>
                <Typography style={{ color: props.theme.colors.neutral30 }} variant="body2">
                  {props.data.subtitle}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      ) : (
        <Box className={MenuItemLabelClass} data-cy="option" width="100%">
          {props.children}
        </Box>
      )}
    </MenuItem>
  );
};

const Placeholder = (props) => {
  const isFocused = props.selectProps.menuIsOpen && props.isFocused;
  return (
    <Typography
      color="inherit"
      className={classNames(
        props.selectProps.classes.placeholder,
        isFocused && props.selectProps.classes.placeholderFocused,
      )}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

const SingleValue = props => (
  <Typography
    className={classNames(
      props.selectProps.classes.singleValue,
      props.selectProps.wrap ? props.selectProps.classes.wrap : null)}
    color="inherit"
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

const ValueContainer = props => (
  <div
    className={classNames(
      props.selectProps.classes.valueContainer,
      props.selectProps.wrap ? props.selectProps.classes.wrap : null,
    )}
    data-cy={props.selectProps['data-cy']}
  >
    {props.children}
  </div>
);

const MultiValue = props => (
  <Chip
    tabIndex={-1}
    label={props.children}
    className={classNames(props.selectProps.classes.chip, {
      [props.selectProps.classes.chipFocused]: props.isFocused,
    })}
    onDelete={props.selectProps.isChipClearable === false ? undefined : props.removeProps.onClick}
    deleteIcon={<Icon icon={XIcon} {...props.removeProps} />}
    size="small"
    style={props.selectProps.chipStyles}
  />
);

// Compose the built-in Menu so getStyles('menu', props) runs — that applies placement (top/bottom)
// from menuPlacement. Replacing Menu with only <Paper> skipped those styles, so "auto" had no effect.
const Menu = (props) => {
  const listboxId = props.selectProps.inputId ? `${props.selectProps.inputId}-listbox` : undefined;

  return (
    <ReactSelectComponents.Menu {...props}>
      <Paper
        square
        className={props.selectProps.classes.paper}
        id={listboxId}
        role="listbox"
      >
        {props.children}
      </Paper>
    </ReactSelectComponents.Menu>
  );
};

const ClearIndicator = props => (
  // Render the default ClearIndicator but add our custom class
  <div className="pm-clear-indicator">
    <ReactSelectComponents.ClearIndicator {...props} />
  </div>
);

const Input = props => (
  <ReactSelectComponents.Input
    {...props}
    autoComplete={props.selectProps.autoComplete}
  />
);

/*
  Reference react-select when setting additional options: https://github.com/JedWatson/react-select
 */
class Select extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      focusedOptionId: null,
      menuIsOpen: false,
    };
    this.fixAccessibility = this.fixAccessibility.bind(this);
    this.getTitle = this.getTitle.bind(this);
    this.handleFocusOption = this.handleFocusOption.bind(this);
    this.wrapperRef = React.createRef();
    this.selectComponentRef = React.createRef();
    this.accessibilityTimeout = null;
  }

  componentDidMount() {
    this.accessibilityTimeout = setTimeout(() => this.fixAccessibility(), 250);
  }

  componentDidUpdate() {
    this.fixAccessibility();
  }

  componentWillUnmount() {
    clearTimeout(this.accessibilityTimeout);
  }

  getAriaLabel() {
    if (this.props['aria-labelledby']) {
      return undefined;
    }
    if (this.props['aria-label']) {
      return this.props['aria-label'];
    }
    const title = this.getTitle();
    if (title && typeof title === 'string') {
      return title;
    }
    return this.props.placeholder;
  }

  getTitle() {
    const selectTitle = this.props.title;
    const required = this.props.intl.formatMessage({ defaultMessage: 'Required' });
    return selectTitle && this.props.isRequiredTitleLabel ?
      `${selectTitle} (${required})` :
      selectTitle;
  }

  handleFocusOption(optionId) {
    this.setState({ focusedOptionId: optionId }, () => {
      this.fixAccessibility({ isExpanded: this.state.menuIsOpen });
    });
  }

  fixAccessibility(options = { isExpanded: false }) {
    const wrapperRef = this.wrapperRef.current;
    const { isExpanded } = options;
    if (wrapperRef) {
      // Fix dummy input provided to non-searchable selects
      // An aria-live region reads out when the input is focused, but Lighthouse complains anyway
      setAccessibilityAttributes(
        wrapperRef,
        this.getAriaLabel(),
        this.props['aria-labelledby'],
        isExpanded,
        this.state.focusedOptionId,
        this.props['aria-required'],
      );

      // Allow focusing on X clear symbol
      // Fix for https://github.com/JedWatson/react-select/issues/4988
      const selectComponentRef = this.selectComponentRef.current;
      makeClearValueIndicatorFocusable(wrapperRef, selectComponentRef, this.props.clearAriaLabel);
    }
  }

  render() {
    const Component = this.props.async ? ReactSelectAsync : ReactSelect;
    const optionComponent = typeof this.props.optionComponent === 'function' ?
      this.props.optionComponent :
      Option;

    if (!Component) {
      return null;
    }
    let value;
    if (this.props.options) {
      if (this.props.isMulti) {
        value = (this.props.value || []).map(v => (
          this.props.options.find(option => option.value === v) || { label: v, value: v }
        ));
      } else {
        value = this.props.options.find(
          option => option.value === this.props.value,
        ) || this.props.value;
      }
    } else {
      value = this.props.value;
    }

    return (
      <div
        className={classNames(this.props.className, this.props.classes.root)}
        ref={this.wrapperRef}
      >
        <Component
          ref={this.selectComponentRef}
          aria-label={this.getAriaLabel()}
          aria-labelledby={this.props['aria-labelledby']}
          autoComplete={this.props.autoComplete}
          cacheOptions={this.props.async ? true : undefined}
          chipStyles={this.props.chipStyles}
          classNamePrefix={SELECT_CLASS_NAME_PREFIX}
          {...(this.props.maxMenuHeight ? { maxMenuHeight: this.props.maxMenuHeight } : {})}
          classes={{
            ...this.props.classes,
            input: classNames(
              this.props.classes.input,
              this.props.classes[`input-${this.props.variant}`],
              this.props.inputClassName,
              this.props.disabled ? this.props.classes.disabled : null,
            ),
          }}
          components={{
            ClearIndicator,
            Control,
            Input,
            Menu,
            MultiValue,
            NoOptionsMessage,
            Option: optionComponent,
            Placeholder,
            SingleValue,
            ValueContainer,
            ...(this.props.hideDropdownIcon ? {
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            } : {}),
            ...this.props.components,
          }}
          data-cy={this.props['data-cy']}
          defaultOptions={this.props.async ? true : undefined}
          hideSelectedOptions={this.props.hideSelectedOptions}
          hoverOptions={this.props.hoverOptions}
          innerRef={this.props.innerRef}
          isClearable={typeof this.props.isClearable !== 'undefined' ? this.props.isClearable : this.props.isMulti}
          isChipClearable={this.props.isChipClearable}
          isDisabled={this.props.disabled}
          isLoading={this.props.isLoading}
          isMulti={this.props.isMulti}
          isSearchable={this.props.isSearchable}
          instanceId={this.props.name}
          inputId={this.props.name ? `react-select-${this.props.name}` : undefined}
          loadOptions={this.props.loadOptions}
          name={this.props.name}
          noOptionsMessageProps={{
            label: this.props.noOptionsMessage,
          }}
          onChange={this.props.onChange}
          onFocusOption={this.handleFocusOption}
          onInputChange={this.props.onInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !this.state.menuIsOpen) {
              e.preventDefault();
              this.setState({ menuIsOpen: true });
            }
          }}
          onMenuClose={() => {
            this.setState({ focusedOptionId: null, menuIsOpen: false }, () => {
              this.fixAccessibility({ isExpanded: false });
            });
            if (this.props.onMenuClose) this.props.onMenuClose();
          }}
          onMenuOpen={() => {
            this.setState({ menuIsOpen: true });
            this.fixAccessibility({ isExpanded: true });
            if (this.props.onMenuOpen) this.props.onMenuOpen();
          }}
          onMenuScrollToBottom={this.props.onMenuScrollToBottom}
          options={this.props.options}
          paginationHasMore={this.props.paginationHasMore}
          paginationIsLoadingMore={this.props.paginationIsLoadingMore}
          placeholder={this.props.placeholder}
          role="combobox"
          simpleValue
          startIcon={this.props.startIcon}
          startIconProps={this.props.startIconProps}
          menuPlacement={this.props.menuPlacement}
          menuPosition={this.props.menuPosition}
          menuPortalTarget={this.props.menuPortalTarget}
          menuIsOpen={this.state.menuIsOpen}
          styles={{
            clearIndicator: () => ({
              color: this.props.theme.palette.text.secondary,
            }),
            dropdownIndicator: () => ({
              color: this.props.theme.palette.secondary.main,
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            input: base => ({
              ...base,
              '& input': {
                color: 'inherit',
                font: 'inherit',
              },
              color: 'inherit',
            }),
            menu: base => ({
              ...base,
              backgroundColor: 'transparent',
              borderRadius: 0,
              boxShadow: 'none',
              zIndex: 11,
            }),
            menuPortal: base => ({ ...base, zIndex: 9999999 }),
          }}
          textFieldProps={{
            'data-testid': this.props['data-testid'],
            'data-tour-id': this.props['data-tour-id'],
            error: this.props.error,
            InputLabelProps: {
              className: this.props.disabled ? this.props.classes.disabledLabel : null,
              htmlFor: this.props.name ? `react-select-${this.props.name}-input` : undefined,
              shrink: true,
            },
            label: this.getTitle(),
            variant: this.props.variant,
          }}
          value={value}
          wrap={this.props.wrap}
          filterOption={this.props.filterOption}
        />
        {this.props.helperText && (
          <FormHelperText variant="outlined">
            {this.props.helperText}
          </FormHelperText>
        )}
      </div>
    );
  }
}

Select.defaultProps = {
  'aria-label': null,
  'aria-labelledby': null,
  'aria-required': false,
  async: false,
  autoComplete: undefined,
  chipStyles: {},
  className: null,
  clearAriaLabel: null,
  components: {},
  'data-cy': null,
  'data-tour-id': null,
  disabled: false,
  filterOption: undefined,
  helperText: null,
  hideDropdownIcon: false,
  hideSelectedOptions: false,
  hoverOptions: false,
  innerRef: undefined,
  inputClassName: null,
  isChipClearable: undefined,
  isClearable: undefined,
  isLoading: false,
  isMulti: false,
  isRequiredTitleLabel: false,
  isSearchable: true,
  loadOptions: undefined,
  maxMenuHeight: null,
  menuPlacement: 'auto',
  menuPortalTarget: undefined,
  menuPosition: 'absolute',
  name: null,
  noOptionsMessage: 'No options',
  onInputChange: null,
  onMenuClose: null,
  onMenuOpen: null,
  onMenuScrollToBottom: null,
  options: undefined,
  paginationHasMore: undefined,
  paginationIsLoadingMore: false,
  placeholder: null,
  startIcon: null,
  startIconProps: null,
  title: undefined,
  value: null,
  variant: 'outlined',
  wrap: false,
};

Select.propTypes = {
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'aria-required': PropTypes.bool,
  async: PropTypes.bool,
  autoComplete: PropTypes.string,
  chipStyles: PropTypes.object,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  clearAriaLabel: PropTypes.string,
  components: PropTypes.object,
  'data-cy': PropTypes.string,
  'data-tour-id': PropTypes.string,
  disabled: PropTypes.bool,
  filterOption: PropTypes.func,
  helperText: PropTypes.string,
  hideDropdownIcon: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  hoverOptions: PropTypes.bool,
  innerRef: PropTypes.func,
  inputClassName: PropTypes.string,
  isChipClearable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isRequiredTitleLabel: PropTypes.bool,
  isSearchable: PropTypes.bool,
  loadOptions: PropTypes.func,
  maxMenuHeight: PropTypes.number,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(['absolute', 'fixed']),
  name: PropTypes.string,
  // MaybeFormattedMessage
  noOptionsMessage: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onMenuScrollToBottom: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  })),
  paginationHasMore: PropTypes.bool,
  paginationIsLoadingMore: PropTypes.bool,
  // MaybeFormattedMessage
  placeholder: PropTypes.node,
  startIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  startIconProps: PropTypes.object,
  theme: PropTypes.object.isRequired,
  // MaybeFormattedMessage
  title: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ])),
    PropTypes.bool,
    PropTypes.number,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    }),
    PropTypes.string,
  ]),
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  wrap: PropTypes.bool,
};

const SelectWrapper = props => <Select {...props} intl={useContext(IntlContext)} t={useIntl()} />;

export default withTheme(withStyles(selectStyles, { withTheme: true })(SelectWrapper));
